/**
 * ETKİN ÇALIŞMA ALANI ADRESTE TAŞINIR.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden çerez yetmedi
 * ═══════════════════════════════════════════════════════════════════════════
 * Etkin alan bir dönem yalnız `mentorivo_ws` çerezindeydi. Çerez tarayıcı
 * başına tek olduğu için üç şey birden imkânsızdı:
 *
 *   • İki sekmede iki farklı çalışma alanı açmak. A sekmesinde alan
 *     değiştirmek, B sekmesindeki tabloyu sessizce başka bir kiracının
 *     verisiyle beslerdi.
 *   • Bir kampanya linkini ekip arkadaşına yollamak. Link karşı tarafın
 *     KENDİ etkin alanında açılırdı — aynı adres iki kişide iki farklı şey.
 *   • Geri tuşuyla alan değişimini geri almak.
 *
 * Artık adres alanı taşıyor:
 *
 *     /tr/dashboard/w/blesyum/campaigns/abc123
 *              └──────┬──────┘
 *          rota segmenti: app/[locale]/dashboard/w/[ws]/campaigns/[id]
 *
 * Çerezin tek işi kaldı: `/dashboard` çıplak adresine gelen kullanıcıyı en
 * son kullandığı alana yollamak.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Sunucu bileşenleri alanı BAŞLIKTAN okuyor — neden params'tan değil
 * ═══════════════════════════════════════════════════════════════════════════
 * `params.ws` her sayfaya geliyor ve doğru kaynak O. Ama `serverApiGet`
 * yüz ayrı yerden çağrılıyor ve hepsine `ws` parametresi eklemek, bir
 * gün birinde unutulacak bir konvansiyon olurdu — unutulduğunda da hata
 * SESSİZ olurdu: istek çereze düşer, yani ÇOĞU ZAMAN doğru sonucu verir ve
 * yalnız ikinci sekmede yanlış kiracıyı gösterir.
 *
 * Bu yüzden proxy adresi ayrıştırıp aşağıdaki başlığı isteğe yazıyor;
 * `serverApiGet` onu okuyup API'ye `x-workspace-id` olarak geçiriyor.
 * Tek yer, unutulamaz.
 *
 * next-intl'in middleware'i iletilen istek başlıklarını GELEN istekten
 * türetiyor (`new Headers(request.headers)`), bu yüzden proxy'de eklenen
 * başlık RSC'ye kadar taşınıyor — belgelenmemiş bir iç mekanizmaya
 * dayanmıyoruz.
 */

/**
 * Proxy → sunucu bileşeni arasındaki İÇ başlık.
 *
 * API'ye giden `x-workspace-id`den bilerek AYRI bir ad: ikisi aynı olsaydı,
 * dışarıdan gelen bir isteğin bu başlığı taşıyıp taşımadığı sorusu
 * karışırdı. Bu başlığı yalnız proxy yazıyor ve yalnız kendi sunucumuz
 * okuyor.
 */
export const WS_ROUTE_HEADER = 'x-mentorivo-ws';

/// Panelin çalışma alanı taşıyan bölümünün öneki (locale önekinden SONRA).
export const WS_SEGMENT = '/dashboard/w/';

/**
 * Çalışma alanı slug'ı: harf/rakam/tire. `uniqueSlug` üreticisi bunun
 * dışına çıkmıyor ve daha geniş bir kalıp, adres çubuğuna yazılan çöpü
 * "slug" sanıp API'ye taşımak olurdu.
 */
const SLUG = /^[a-z0-9][a-z0-9-]{0,80}$/;

/**
 * Locale öneki SOYULMUŞ bir yoldan alan slug'ını çıkarır.
 *
 * `/dashboard/w/blesyum/campaigns` → `blesyum`
 * `/dashboard`                     → `null`
 * `/dashboard/w`                   → `null`
 */
export function workspaceFromPath(path: string): string | null {
  if (!path.startsWith(WS_SEGMENT)) return null;

  const rest = path.slice(WS_SEGMENT.length);
  const slug = rest.split('/')[0] ?? '';
  return SLUG.test(slug) ? slug : null;
}

/**
 * Panel içi bir adrese alan segmentini ekler.
 *
 * `('/dashboard/campaigns', 'blesyum')` → `/dashboard/w/blesyum/campaigns`
 *
 * Zaten alan taşıyan adres OLDUĞU GİBİ dönüyor: `Link` sarmalayıcısı her
 * href'ten geçiyor ve iki kez eklemek `/w/blesyum/w/blesyum/…` üretirdi.
 * Panel dışı adresler (`/login`, `/`) de dokunulmadan geçiyor.
 */
export function withWorkspace(path: string, slug: string | null): string {
  if (!slug) return path;
  if (!path.startsWith('/dashboard')) return path;
  if (path.startsWith(WS_SEGMENT)) return path;

  return `${WS_SEGMENT}${slug}${path.slice('/dashboard'.length)}`;
}

/**
 * Adresten alan segmentini SÖKER — sol menünün "hangi bağlantı etkin"
 * kontrolü rotayı alan olmadan tanıyor.
 *
 * `/dashboard/w/blesyum/campaigns` → `/dashboard/campaigns`
 */
export function withoutWorkspace(path: string): string {
  const slug = workspaceFromPath(path);
  if (!slug) return path;

  const rest = path.slice(WS_SEGMENT.length + slug.length);
  return `/dashboard${rest}`;
}
